import apiAxios from "./axiosInstances/apiAxios";
import JwtService from "@/core/services/JwtService";
import router from "@/router";
import { errorToast } from "../helpers/utility";

class ApiService {
  // Error tracking properties
  static pendingErrors = new Set();
  static hasNetworkError = false;
  static errorToastTimeout = null;
  static isShowingError = false;

  // Network retry properties
  static activeNetworkRetries = new Map();
  static maxRetries = 5;
  static retryInterval = 5000;

  // Refresh token
  static refreshQueue = [];
  static isRefreshing = false;

  static init() {
    // Set base URL if not already configured
    if (!apiAxios.defaults.baseURL) {
      apiAxios.defaults.baseURL = import.meta.env.VITE_APP_BASEURL;
    }

    // Set default headers
    apiAxios.defaults.headers.common["Accept"] = "application/json";

    // Initialize interceptors
    ApiService.setupRequestInterceptor();
    ApiService.setupResponseInterceptor();

    // Set initial auth header
    ApiService.setAuthorizationHeader();
  }

  static setupRequestInterceptor() {
    apiAxios.interceptors.request.use(
      (config) => {
        const token = JwtService.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );
  }

  static setupResponseInterceptor() {
    apiAxios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const status = error.response?.status;

        // Let refresh-token request failures bubble up to refreshToken() caller.
        if (originalRequest?._isRefreshRequest) {
          return Promise.reject(error);
        }

        // Add flag to track if we've handled this error
        if (originalRequest && !originalRequest._errorHandled) {
          const errorMessage =
            error.response?.data?.message || "An error occurred";

          if (status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            // Add request to queue while refreshing
            if (ApiService.isRefreshing) {
              return new Promise((resolve, reject) => {
                this.refreshQueue.push({ resolve, reject });
              }).then(() => {
                return apiAxios(originalRequest);
              });
            }

            ApiService.isRefreshing = true;

            try {
              const newToken = await ApiService.refreshToken();

              if (newToken) {
                // Update auth header and retry original request
                ApiService.setAuthorizationHeader();
                originalRequest.headers.Authorization = `Bearer ${newToken}`;

                // Retry all queued requests
                ApiService.processQueue(null, newToken);
                return apiAxios(originalRequest);
              }

              throw new Error("Token refresh failed");
            } catch (refreshError) {
              // Clear queue and tokens if refresh fails
              ApiService.processQueue(refreshError);
              ApiService.handleUnauthorizedError();
              return Promise.reject(error);
            } finally {
              ApiService.isRefreshing = false;
            }
          } else if (status === 403) {
            originalRequest._errorHandled = true;
            ApiService.handleForbiddenError(error.response?.data?.message);
          } else {
            originalRequest._errorHandled = true;
            if (error.response) {
              errorToast(errorMessage, status);
            } else {
              // errorToast("Network Error - Please check your connection");
              if (!ApiService.hasNetworkError) {
                ApiService.hasNetworkError = true;
                // this.attemptNetworkRetry(originalRequest);
                ApiService.addPendingError("NETWORK_ERROR");
              }
            }
          }
        }

        return Promise.reject(error);
      },
    );
  }

  // IN CASE WE MIGHT NEED THIS SOMEDAY
  // TO AUTO-REFRESH AND ENDPOINT THAT FAILS WITH NETWORK
  // IT DOESNT WORK WITH MULTIPLE-ENDPOINTS YET
  // MIGHT NOT BE SCALABLE BECAUSE OF HOW WE CALL ENDPOINTS
  // AND ALSO TO SAVE PC RESOURCES, LET THE DO A MANUAL REFRESH FOR NOW
  static attemptNetworkRetry(originalRequest) {
    const requestKey = `${originalRequest.method}-${originalRequest.url}`;

    if (this.activeNetworkRetries.has(requestKey)) return;

    let retryCount = 0;
    this.activeNetworkRetries.set(requestKey, true);

    const retry = async () => {
      retryCount++;

      // Show toast for this attempt
      errorToast(
        `Network Error - Retrying... (${retryCount}/${this.maxRetries})`,
      );

      try {
        const retryConfig = {
          ...originalRequest,
          _retryCount: retryCount,
          _errorHandled: false,
        };

        const response = await apiAxios(retryConfig);

        // Success - clean up
        this.activeNetworkRetries.delete(requestKey);
        this.hasNetworkError = false;
        return response;
      } catch (error) {
        if (retryCount < this.maxRetries) {
          setTimeout(retry, this.retryInterval);
        } else {
          // Final failure
          this.activeNetworkRetries.delete(requestKey);
          this.hasNetworkError = false;
          errorToast(
            "Network Error - All retries failed. Please check your connection and refresh page.",
          );
        }
      }
    };

    // Start first retry
    retry();
  }

  static addPendingError(errorKey) {
    this.pendingErrors.add(errorKey);
    this.debounceErrorToast();
  }

  static debounceErrorToast() {
    clearTimeout(this.errorToastTimeout);

    this.errorToastTimeout = setTimeout(() => {
      if (this.pendingErrors.size > 0 && !this.isShowingError) {
        this.isShowingError = true;

        let toastMessage;
        if (this.pendingErrors.has("NETWORK_ERROR")) {
          toastMessage =
            "Network Error - Please check your internet connection and refresh page";
        } else {
          const messages = Array.from(this.pendingErrors)
            .filter((err) => err !== "NETWORK_ERROR")
            .map((msg) =>
              msg.startsWith("HTTP_")
                ? `Server error (${msg.replace("HTTP_", "")})`
                : msg,
            );

          toastMessage = messages.join("\n") || "An error occurred";
        }

        errorToast(toastMessage);
        this.isShowingError = false;
        this.pendingErrors.clear();
        this.hasNetworkError = false;
      }
    }, 3000); // Debounce period
  }

  static setAuthorizationHeader() {
    const token = JwtService.getToken();
    if (token) {
      apiAxios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete apiAxios.defaults.headers.common["Authorization"];
    }
  }

  static handleForbiddenError(message) {
    // Handle multi-subscription error toast uniquely
    if (message == "Subscription Expired") {
      ApiService.addPendingError(message);
    } else
      errorToast(
        message ||
          "You cannot perform this action. If this is a mistake, please contact the admin.",
      );
  }

  static handleUnauthorizedError() {
    import("@/store/Auth")
      .then(({ useAuthStore }) => {
        const authStore = useAuthStore();
        authStore.purgeAuth();
      })
      .catch(() => {
        JwtService.destroyToken();
        JwtService.destroyRefreshToken();
        localStorage.clear();
      })
      .finally(() => {
        if (router.currentRoute.value?.name !== "login") {
          router.push({ name: "login" });
        }
      });
  }

  static processQueue(error, newToken = null) {
    ApiService.refreshQueue.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve(newToken);
      }
    });
    ApiService.refreshQueue = [];
  }

  static async refreshToken() {
    try {
      const refreshToken = JwtService.getRefreshToken();
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await apiAxios.post(
        "/account/refreshToken",
        {
          token: JwtService.getToken(),
          refreshToken: refreshToken,
        },
        {
          _isRefreshRequest: true,
        },
      );

      const { token: newToken, refreshToken: newRefreshToken } = response.data;

      // Save the new tokens
      JwtService.saveToken(newToken);
      JwtService.saveRefreshToken(newRefreshToken);
      return newToken;
    } catch (error) {
      console.error("Token refresh failed:", error);
      throw error;
    }
  }

  // HTTP Methods
  static get(resource, { params = {}, fileType = null } = {}) {
    const config = {
      method: "get",
      url: resource,
      params,
    };

    if (fileType) {
      config.headers = this.getFileHeaders(fileType);
      config.responseType = "blob";
    }

    return apiAxios(config);
  }

  static post(resource, params) {
    return this.handleDataRequest("post", resource, params);
  }

  static put(resource, params) {
    return this.handleDataRequest("put", resource, params);
  }

  static patch(resource, params) {
    return this.handleDataRequest("patch", resource, params);
  }

  static delete(resource) {
    return apiAxios.delete(resource);
  }

  // Helper Methods
  static handleDataRequest(method, resource, params) {
    const config = {
      method,
      url: resource,
      data: params,
    };

    if (params instanceof FormData) {
      config.headers = {
        "Content-Type": "multipart/form-data",
      };
    }

    return apiAxios(config);
  }

  static getFileHeaders(fileType) {
    const type = fileType.toLowerCase();
    const headers = {};

    switch (type) {
      case "excel":
        headers["Accept"] =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        headers["Content-Type"] = "application/vnd.ms-excel";
        break;
      case "csv":
        headers["Accept"] = "text/csv";
        headers["Content-Type"] = "text/csv";
        break;
      case "pdf":
        headers["Accept"] = "application/pdf";
        headers["Content-Type"] = "application/pdf";
        break;
      default:
        throw new Error("Unsupported file type");
    }

    return headers;
  }
}

// Initialize the service
ApiService.init();

export default ApiService;
